import { useState, useEffect } from 'react';
import { User, Phone, Mail, MapPin, Calendar, Bed, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';

const ChildProfile = () => {
  const { children, selectedChild, setSelectedChild, loading } = useParentData();
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (selectedChild) {
      fetchProfile(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchProfile = async (studentId) => {
    setLoadingProfile(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}`);
      setProfileData(response.data);
    } catch (error) {
      toast.error('Failed to load child profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  if (loading || loadingProfile) {
    return <div className="p-6 text-center text-gray-500">Loading profile...</div>;
  }

  if (!selectedChild || !profileData) {
    return <div className="p-6 text-center text-gray-500">No child data available</div>;
  }

  const student = profileData.student;
  const warden = profileData.warden;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>My Child</h1>
        <p className="text-gray-600 mt-1">View your child's profile and details</p>
      </div>

      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              {student.photo_url ? (
                <img 
                  src={student.photo_url} 
                  alt={student.name}
                  className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 rounded-lg bg-blue-100 flex items-center justify-center border-2 border-gray-200">
                  <User className="w-16 h-16 text-blue-600" />
                </div>
              )}

              <div className="flex-1 space-y-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
                    {student.name}
                  </h2>
                  <p className="text-gray-600">{student.readable_id}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Date of Birth</p>
                      <p className="font-medium">{student.date_of_birth || 'Not set'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Gender</p>
                      <p className="font-medium capitalize">{student.gender || 'Not set'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Class</p>
                      <p className="font-medium">{student.class_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Bed className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Room Number</p>
                      <p className="font-medium">{student.room_number}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Warden Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Assigned Warden
            </CardTitle>
          </CardHeader>
          <CardContent>
            {warden ? (
              <div>
                <p className="font-semibold text-lg" style={{ color: '#2C5F7F' }}>
                  {warden.name}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No warden assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional Details */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#2C5F7F' }}>Father's Details</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Name:</span> <span className="font-medium">{student.father_name || 'Not provided'}</span></p>
                <p><span className="text-gray-500">Phone:</span> <span className="font-medium">{student.primary_phone || 'Not provided'}</span></p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3" style={{ color: '#2C5F7F' }}>Mother's Details</h4>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Name:</span> <span className="font-medium">{student.mother_name || 'Not provided'}</span></p>
                <p><span className="text-gray-500">Phone:</span> <span className="font-medium">{student.secondary_phone || 'Not provided'}</span></p>
              </div>
            </div>

            {student.address && (
              <div className="md:col-span-2">
                <h4 className="font-semibold mb-2" style={{ color: '#2C5F7F' }}>Address</h4>
                <p className="text-sm text-gray-600">{student.address}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChildProfile;
